'use client';

import { ChevronDown, MapPin } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useNativeTokens } from '~/(tenant)/_components/native/_lib/use-native-tokens';
import {
  type VenueOption,
  useVenueLabel,
  useVenuePicker,
} from '~/(tenant)/_components/venue-picker/use-venue-picker';
import { VenuePickerSurface } from '~/(tenant)/_components/venue-picker/venue-picker-surface';
import { shouldPromptNativeFirstPick } from '~/(tenant)/_components/venue-picker/venue-picker-routes';
import type { PublicLocationRow } from '~/(tenant)/book/_lib/server/booking-page.loader';

interface TopBarLocationSwitcherProps {
  /** Tenant UUID — namespaces the cookie so multi-tenant users keep distinct
   *  preferences. */
  accountId: string;
  /** All bookable locations for the tenant. Component self-hides when there
   *  are fewer than two (nothing to switch to). */
  locations: VenueOption[];
  /** School networks: pick through the /book Region → City → Club funnel. */
  funnelLocations?: PublicLocationRow[];
}

/**
 * Compact venue switcher rendered in the native top bar (right side).
 * Tap to open a draggable native-feeling bottom drawer (powered by vaul via
 * @kit/ui/drawer). First-time visitors on a venue-aware route get the sheet
 * auto-opened with "pick your venue" copy; it stays drag-to-close (a sheet
 * that refuses to close reads as a frozen app). Picking/persistence lives in
 * useVenuePicker, shared with the web picker.
 *
 * Single-location tenants get nothing rendered — there's no useful action.
 */
export function TopBarLocationSwitcher({
  accountId,
  locations,
  funnelLocations,
}: TopBarLocationSwitcherProps) {
  const t = useNativeTokens();
  const { t: tr } = useTranslation('bookings');
  const labelFor = useVenueLabel();
  const picker = useVenuePicker({
    accountId,
    venues: locations,
    shouldPromptFirstPick: shouldPromptNativeFirstPick,
  });

  // Before the first pick the chip shows the alphabetic first venue, which is
  // also what the page falls back to rendering.
  const displayed = picker.activeVenue ?? picker.venues[0];
  if (picker.venues.length < 2 || !displayed) return null;

  return (
    <>
      <button
        type="button"
        onClick={picker.open}
        className="flex h-8 items-center gap-1 rounded-full px-2.5 transition-opacity active:opacity-70"
        style={{
          color: t.foreground,
          background: `${t.foreground}10`,
          maxWidth: 160,
        }}
        aria-label={tr('venueSwitcher.changeLocation', {
          defaultValue: 'Change location',
        })}
      >
        <MapPin
          size={13}
          strokeWidth={1.5}
          style={{ opacity: 0.75, flexShrink: 0 }}
        />
        <span
          style={{
            fontFamily: t.fontDisplay,
            fontSize: 12.5,
            fontWeight: 500,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
          }}
        >
          {labelFor(displayed).name}
        </span>
        <ChevronDown
          size={13}
          strokeWidth={1.5}
          style={{ opacity: 0.6, flexShrink: 0 }}
        />
      </button>

      <VenuePickerSurface
        picker={picker}
        variant="sheet"
        funnelLocations={funnelLocations}
      />
    </>
  );
}
